'use client'

import { useRouter } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'
import { buildUrl } from '../helpers'

export const FilterMenu = ({
  currentBrand,
  brands,
  sortSlug,
}: FilterMenuProps) => {
  const router = useRouter()
  const [isChanging, setIsChanging] = useState(false)
  const previousBrandRef = useRef(currentBrand)

  // Reset loading state when props change (navigation completed)
  useEffect(() => {
    if (previousBrandRef.current !== currentBrand) {
      previousBrandRef.current = currentBrand
      // Defer state update to avoid synchronous setState in effect
      queueMicrotask(() => {
        setIsChanging(false)
      })
    }
  }, [currentBrand])

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const brandSlug = e.target.value
    if (brandSlug === currentBrand) return // Don't navigate if same brand
    setIsChanging(true)
    const newUrl = buildUrl(brandSlug, sortSlug, false, 1) // Reset to page 1 when changing brand, default to in-stock only
    router.push(newUrl)
  }

  return (
    <div className="w-full">
      <label
        htmlFor="brand-filter"
        className="block text-sm font-medium text-gray-700 mb-2"
      >
        Filter by brand
      </label>
      <div className="relative">
        <select
          id="brand-filter"
          className={`w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-vi-red focus:border-vi-red bg-white hover:bg-gray-50 transition-all duration-200 cursor-pointer ${
            isChanging ? 'opacity-60 pointer-events-none' : ''
          }`}
          value={currentBrand}
          onChange={handleBrandChange}
          disabled={isChanging}
        >
          <option value="all">All brands</option>
          {brands.map(brand => (
            <option key={brand.slug} value={brand.slug}>
              {brand.name}
            </option>
          ))}
        </select>
        {isChanging && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-vi-red border-t-transparent" />
          </div>
        )}
      </div>
    </div>
  )
}

interface FilterMenuProps {
  currentBrand: string
  brands: { name: string; slug: string }[]
  sortSlug: string
}
